import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import {
  AuthProvider,
  ChatHistoryProvider,
  ProfileProvider,
  ToastProvider,
  NotificationProvider,
  useAuth,
} from './hooks';
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  ChatPage,
  AdminPage,
  MyPage,
  PrivacyPolicyPage,
  TermsPage,
  TermsCompanyPage,
  TermsStudentPage,
  PrivacyCompanyPage,
  PrivacyStudentPage,
  AccountDeletionPage,
  AccountDeletedPage,
  CognitoCallbackPage,
} from './pages';
import {
  CompanyLoginPage,
  CompanyDashboardPage,
  CompanyRegisterPage,
  CompanyManagementPage,
  CompanyProfilePage,
  EmployeeAccessRestrictedPage,
  MessageInboxPage as CompanyMessageInboxPage,
  StudentSearchPage,
  ScoutComposerPage,
  ScoutHistoryPage,
  TimelineManagementPage,
} from './pages/company';
import CompanySponsorSettingsPage from './pages/company/CompanySponsorSettingsPage';
import {
  MessageInboxPage as StudentMessageInboxPage,
  MessageDetailPage,
  TimelineViewPage,
} from './pages/student';
import CompanyProfileViewPage from './pages/student/CompanyProfileViewPage';
import CompanyListPage from './pages/student/CompanyListPage';
import { StudentSettings } from './pages/StudentSettings';
import AdminUserManagementPage from './pages/admin/AdminUserManagementPage';
import AdminCompanyManagementPage from './pages/admin/AdminCompanyManagementPage';
import AdminCompanyPreviewPage from './pages/admin/AdminCompanyPreviewPage';
import EventCalendarPage from './pages/EventCalendarPage';
import AdminEventFormPage from './pages/admin/AdminEventFormPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import CompanyNotificationsPage from './pages/company/CompanyNotificationsPage';
import TaskListPage from './pages/student/TaskListPage';
// DashboardPage import removed - page hidden from users

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
}> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // ロール制限がある場合のチェック
  if (allowedRoles && user && !allowedRoles.includes(user.userRole)) {
    // 企業管理者とHRはダッシュボードへ
    if (user.userRole === 'company_admin' || user.userRole === 'hr') {
      return <Navigate to="/company/dashboard" replace />;
    }
    // 企業従業員にはアクセス制限ページを表示
    if (user.userRole === 'employee') {
      return <Navigate to="/company/access-restricted" replace />;
    }
    // 管理者は管理画面にリダイレクト
    if (user.userRole === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    // その他は学生向けチャットにリダイレクト
    return <Navigate to="/chat" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (only for non-authenticated users)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated && user) {
    // Redirect based on user role
    if (user.userRole === 'company_admin' || user.userRole === 'hr') {
      // 企業管理者とHRは企業ダッシュボードへ
      return <Navigate to="/company/dashboard" replace />;
    } else if (user.userRole === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user.userRole === 'employee') {
      return <Navigate to="/company/access-restricted" replace />;
    } else {
      // Default to chat for students and other users
      return <Navigate to="/chat" replace />;
    }
  }

  return <>{children}</>;
};

// 学生専用ルートガード
const StudentRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRoles={['student']}>
    {children}
  </ProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <ProfileProvider>
        <ToastProvider>
          <ChatHistoryProvider>
            <NotificationProvider>
            <Router>
              <ScrollToTop />
              <Box sx={{ minHeight: '100vh' }}>
                <Routes>
                  {/* Authentication Routes */}
                  <Route
                    path="/auth/login"
                    element={
                      <PublicRoute>
                        <LoginPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/auth/register"
                    element={
                      <PublicRoute>
                        <RegisterPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/company/register"
                    element={
                      <PublicRoute>
                        <CompanyRegisterPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/auth/forgot-password"
                    element={
                      <PublicRoute>
                        <ForgotPasswordPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/auth/reset-password"
                    element={
                      <PublicRoute>
                        <ResetPasswordPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/verify-email"
                    element={
                      <PublicRoute>
                        <VerifyEmailPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/auth/callback"
                    element={<CognitoCallbackPage />}
                  />

                  {/* Legal Pages */}
                  <Route
                    path="/privacy-policy"
                    element={<PrivacyPolicyPage />}
                  />
                  <Route path="/terms" element={<TermsPage />} />
                  
                  {/* Company Legal Pages */}
                  <Route path="/terms/company" element={<TermsCompanyPage />} />
                  <Route path="/privacy/company" element={<PrivacyCompanyPage />} />
                  
                  {/* Student Legal Pages */}
                  <Route path="/terms/student" element={<TermsStudentPage />} />
                  <Route path="/privacy/student" element={<PrivacyStudentPage />} />

                  {/* Account Deletion Pages */}
                  <Route
                    path="/account-deletion"
                    element={
                      <ProtectedRoute>
                        <AccountDeletionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account-deleted"
                    element={<AccountDeletedPage />}
                  />

                  {/* Protected Routes */}
                  {/* Dashboard route removed - page hidden from users */}

                  {/* Company Routes */}
                  <Route
                    path="/company/login"
                    element={
                      <PublicRoute>
                        <CompanyLoginPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/company/register"
                    element={
                      <PublicRoute>
                        <CompanyRegisterPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/company/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <CompanyDashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/employees"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <CompanyManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/profile"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <CompanyProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/students/search"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <StudentSearchPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/scout/compose"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <ScoutComposerPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/scout/history"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <ScoutHistoryPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Timeline Management Route (Company) */}
                  <Route
                    path="/company/timeline"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <TimelineManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/sponsor"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <CompanySponsorSettingsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Company Message Routes */}
                  <Route
                    path="/company/messages"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <CompanyMessageInboxPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/sponsor"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin']}>
                        <div>スポンサー設定ページ（準備中）</div>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/permissions"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin']}>
                        <div>権限設定ページ（準備中）</div>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/analytics"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin']}>
                        <div>利用状況確認ページ（準備中）</div>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/company/access-restricted"
                    element={
                      <ProtectedRoute allowedRoles={['employee']}>
                        <EmployeeAccessRestrictedPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/chat"
                    element={
                      <ProtectedRoute allowedRoles={['student', 'admin']}>
                        <ChatPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/chat/:sessionId"
                    element={
                      <ProtectedRoute allowedRoles={['student', 'admin']}>
                        <ChatPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={<Navigate to="/admin/dashboard" replace />}
                  />
                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute>
                        <AdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute>
                        <AdminPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Management Routes */}
                  <Route
                    path="/admin/management/companies"
                    element={
                      <ProtectedRoute>
                        <AdminCompanyManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Admin: 学生向け企業一覧プレビュー */}
                  <Route
                    path="/admin/companies-preview"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminCompanyPreviewPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mypage"
                    element={
                      <ProtectedRoute allowedRoles={['student', 'admin']}>
                        <MyPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <StudentSettings />
                      </ProtectedRoute>
                    }
                  />

                  {/* Student Message Routes */}
                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute>
                        <StudentMessageInboxPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages/:threadId"
                    element={
                      <ProtectedRoute>
                        <MessageDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Timeline View Route (Student) */}
                  <Route
                    path="/timeline"
                    element={
                      <ProtectedRoute>
                        <TimelineViewPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Task Management Route (Student only) */}
                  <Route
                    path="/tasks"
                    element={
                      <StudentRoute>
                        <TaskListPage />
                      </StudentRoute>
                    }
                  />

                  {/* Company List Route (Student) */}
                  <Route
                    path="/companies"
                    element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <CompanyListPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Company Profile View Route (Student) */}
                  <Route
                    path="/companies/:companyId/profile"
                    element={
                      <ProtectedRoute>
                        <CompanyProfileViewPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Event Calendar Routes */}
                  <Route
                    path="/events"
                    element={
                      <ProtectedRoute>
                        <EventCalendarPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/events/new"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'company_admin', 'hr']}>
                        <AdminEventFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/events/:id/edit"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'company_admin', 'hr']}>
                        <AdminEventFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/notifications"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminNotificationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/notifications"
                    element={
                      <ProtectedRoute allowedRoles={['company_admin', 'hr']}>
                        <CompanyNotificationsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Default redirects */}
                  <Route path="/" element={<Navigate to="/chat" replace />} />
                  <Route
                    path="*"
                    element={<Navigate to="/auth/login" replace />}
                  />
                </Routes>
              </Box>
            </Router>
            </NotificationProvider>
          </ChatHistoryProvider>
        </ToastProvider>
      </ProfileProvider>
    </AuthProvider>
  );
}

export default App;
